import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import socket from "../../../utils/socket";
import { ArrowLeft, Cpu, Layers, Activity, Server, RefreshCw, AlertCircle } from "lucide-react";
import "./taskmanager.css";

const TaskManager = () => {
  const { id } = useParams();
  const [tasks, setTasks] = useState({ applications: [], background_processes: [] });
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      setError("No agent identifier provided.");
      setLoading(false);
      return;
    }

    const fetchTasks = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/task-manager/${id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });

        const res = await response.json();
        if (res?.success && res?.data?.length) {
          const doc = res.data[0];
          setDevice({
            hostname: doc.device?.hostname || `Agent ${doc.agentId}`,
            os_type: doc.device?.os_type || "Unknown OS",
            os_version: doc.device?.os_version || "",
            machine_id: doc.agentId,
          });
          setTasks({
            applications: doc.data.applications || [],
            background_processes: doc.data.background_processes || [],
          });
        }
        // No data in DB yet — live socket update will populate it
      } catch (e) {
        console.error("API error:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();

    // Ask the backend to request live task_info from the agent
    socket.emit("request_agent_update", { agentId: id, type: "task_info" });

    socket.on("task_info_update", (update) => {
      if (update.agentId === id) {
        setError(null);
        setDevice((prev) => prev || { hostname: `Agent ${id}`, os_type: "", os_version: "", machine_id: id });
        setTasks({
          applications: update.data.applications || [],
          background_processes: update.data.background_processes || [],
        });
      }
    });

    // Re-request when agent comes back online
    socket.on("agent_update", (data) => {
      if (data.agentId === id && data.status === "online") {
        socket.emit("request_agent_update", { agentId: id, type: "task_info" });
      }
    });

    return () => {
      socket.off("task_info_update");
      socket.off("agent_update");
    };
  }, [id]);

  if (loading) return (
    <div className="pc-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
      <RefreshCw size={40} className="animate-spin" style={{ color: '#00b4d8' }} />
      <p style={{ color: '#8ca8b3' }}>Analyzing remote processes...</p>
    </div>
  );

  return (
    <div className="pc-container">
      <div className="task-header">
        <div className="header-left">
          <button className="gc-icon-btn" onClick={() => navigate("/devices")} data-tip="Back to Devices" aria-label="Back to Devices">
            <ArrowLeft size={16} />
          </button>
          <div className="task-title-group">
            <h1 className="task-title">Task Manager</h1>
            <p className="task-sub">Active Node: <span className="task-sub-node">{device?.hostname}</span></p>
          </div>
        </div>
        <div className="header-right">
        </div>
      </div>

      <div className="pc-section">
        <div className="section-header">
          <Server size={18} style={{ color: '#00b4d8' }} />
          <h2>Applications</h2>
        </div>
        <div className="table">
          <div className="table-header">
            <div>Process Name</div>
            <div>PID</div>
            <div>CPU Activity</div>
            <div>Memory Load</div>
          </div>
          <div className="table-body">
            {tasks.applications.length ? (
              tasks.applications.map((app, idx) => (
                <div key={idx} className="task-row">
                  <div className="task-name">
                    <Activity size={14} className="task-icon" />
                    <div className="task-name-group">
                      <span className="task-name-primary">{app.name}</span>
                      {app.title && <span className="task-app-title">{app.title}</span>}
                    </div>
                  </div>
                  <div className="task-stats-row">
                    <div className="task-stat task-pid">{app.pid}</div>
                    <div className="task-stat tm-stat-cpu">{app.cpu_percent}%</div>
                    <div className="task-stat tm-stat-mem">{app.memory_percent}%</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">No foreground applications detected.</div>
            )}
          </div>
        </div>
      </div>

      <div className="pc-section" style={{ marginTop: '30px' }}>
        <div className="section-header">
          <Layers size={18} style={{ color: '#00b4d8' }} />
          <h2>System Sub-processes</h2>
        </div>
        <div className="table">
          <div className="table-header">
            <div>Binary Name</div>
            <div>PID</div>
            <div>CPU Activity</div>
            <div>Memory Load</div>
          </div>
          <div className="table-body">
            {tasks.background_processes.length ? (
              tasks.background_processes.map((proc, idx) => (
                <div key={idx} className="task-row">
                  <div className="task-name">
                    <Cpu size={14} className="task-icon-muted" />
                    {proc.name}
                  </div>
                  <div className="task-stats-row">
                    <div className="task-stat task-pid">{proc.pid}</div>
                    <div className="task-stat tm-stat-cpu">{proc.cpu_percent}%</div>
                    <div className="task-stat tm-stat-mem">{proc.memory_percent}%</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty">No background processes recorded.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskManager;
